import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Users, 
  Calendar, 
  ArrowRight,
  Lock,
  CheckCircle,
  Clock,
  Sparkles,
  MessageCircle,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AjoSavings = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [allGroups, setAllGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchGroups();
    fetchUnread();
  }, []);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ajo/groups/unread-counts`, { headers: getAuthHeaders() });
      setUnreadCounts(res.data.unread || {});
    } catch {}
  };

  const fetchGroups = async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API_URL}/api/ajo/groups`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/ajo/groups/my`, { headers: getAuthHeaders() })
      ]);
      setAllGroups(allRes.data);
      setMyGroups(myRes.data);
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('NGN', '₦');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const myGroupIds = myGroups.map(g => g.id);
  const availableGroups = allGroups.filter(
    g => !myGroupIds.includes(g.id) && g.status === 'pending' && g.members_count < g.group_size
  );

  const matchesSearch = (g) => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return true;
    return (
      (g.name || '').toLowerCase().includes(s) ||
      (g.admin_name || '').toLowerCase().includes(s)
    );
  };
  const filteredMy = myGroups.filter(matchesSearch);
  const filteredAvailable = availableGroups.filter(matchesSearch);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-full rounded-full mb-6" />
          <Skeleton className="h-32 w-full rounded-2xl mb-4" />
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="ajo-savings-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0B1F3A]">Ajo Savings</h1>
          <Button 
            onClick={() => navigate('/ajo/create')}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold btn-press"
            data-testid="create-group-btn"
          >
            <Plus size={18} className="mr-1" />
            Create
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="my" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="my" data-testid="my-groups-tab">
              My Groups ({myGroups.length})
            </TabsTrigger>
            <TabsTrigger value="available" data-testid="available-groups-tab">
              Available Groups ({availableGroups.length})
            </TabsTrigger>
          </TabsList>

          {/* Search bar */}
          <div className="relative mb-6" data-testid="groups-search-wrapper">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search groups by name or creator..."
              className="pl-10 h-11 rounded-xl border-2 border-slate-300 focus:border-[#2272C5] text-sm font-semibold"
              data-testid="groups-search-input"
            />
          </div>

          <TabsContent value="my" className="mt-0">
            {filteredMy.length === 0 ? (
              <EmptyState 
                title={searchTerm ? 'No matches' : 'No groups yet'}
                description={searchTerm ? 'Try a different search term.' : 'Create or join an Ajo group to start saving together'}
                action={() => navigate('/ajo/create')}
                actionText="Create Group"
              />
            ) : (
              <div className="space-y-4">
                {filteredMy.map((group) => (
                  <GroupCard 
                    key={group.id} 
                    group={group} 
                    formatCurrency={formatCurrency}
                    getStatusBadge={getStatusBadge}
                    onClick={() => navigate(`/ajo/${group.id}`)}
                    isMember={true}
                    unreadCount={unreadCounts[group.id] || 0}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="available" className="mt-0">
            {filteredAvailable.length === 0 ? (
              <EmptyState 
                title={searchTerm ? 'No matches' : 'No available groups'}
                description={searchTerm ? 'Try a different search term.' : "All groups are full or you're already a member. Create a new one!"}
                action={() => navigate('/ajo/create')}
                actionText="Create Group"
              />
            ) : (
              <div className="space-y-4">
                {filteredAvailable.map((group) => (
                  <GroupCard 
                    key={group.id} 
                    group={group} 
                    formatCurrency={formatCurrency}
                    getStatusBadge={getStatusBadge}
                    onClick={() => navigate(`/ajo/${group.id}`)}
                    isMember={false}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* How it works */}
        <div className="mt-8 p-4 rounded-2xl bg-[#0B1F3A] text-white">
          <h3 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-emerald-400" />
            How Ajo Works
          </h3>
          <div className="space-y-2 text-white font-medium">
            <p>1. Pay 50% of total payout as lock qualification to join</p>
            <p>2. Make weekly contributions with all members</p>
            <p>3. One member receives the full payout each week</p>
            <p>4. Your deposit is released when the group completes</p>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

const GroupCard = ({ group, formatCurrency, getStatusBadge, onClick, isMember, unreadCount = 0 }) => {
  const progress = (group.members_count / group.group_size) * 100;
  
  return (
    <Card 
      className="glass-card cursor-pointer group-card overflow-hidden relative"
      onClick={onClick}
      data-testid={`group-card-${group.id}`}
    >
      {unreadCount > 0 && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-red-500 text-white px-2 py-0.5 rounded-full" data-testid={`group-unread-${group.id}`}>
          <MessageCircle size={12} />
          <span className="text-xs font-bold">{unreadCount > 99 ? '99+' : unreadCount}</span>
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-slate-900">{group.name}</h3>
              {group.contribution_frequency === 'monthly' && (
                <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">Monthly</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">by {group.admin_name}</p>
          </div>
          {getStatusBadge(group.status)}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-slate-500">
              {group.contribution_frequency === 'monthly' ? 'Monthly' : 'Weekly'} Contribution
            </p>
            <p className="font-semibold text-slate-900">{formatCurrency(group.contribution_amount)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Total Payout</p>
            <p className="font-semibold text-emerald-600">{formatCurrency(group.total_payout)}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <Users size={14} className="text-slate-400" />
            <span className="text-sm text-slate-600">
              {group.members_count}/{group.group_size}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Lock size={14} className="text-amber-500" />
            <span className="text-sm text-slate-600">
              {formatCurrency(group.lock_qualification_amount)} lock deposit
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="contribution-progress">
          <div 
            className="contribution-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {group.members_count === group.group_size 
            ? 'Group is full' 
            : group.group_size - group.members_count <= 2
              ? <span className="text-red-600 font-bold">Only {group.group_size - group.members_count} {group.group_size - group.members_count === 1 ? 'slot' : 'slots'} left!</span>
              : `${group.group_size - group.members_count} spots remaining`}
        </p>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <span className="text-sm text-slate-500">
            {isMember ? 'View details' : 'Join this group'}
          </span>
          <ArrowRight size={16} className="text-slate-400" />
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyState = ({ title, description, action, actionText }) => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
      <Users size={24} className="text-slate-400" />
    </div>
    <h3 className="font-bold text-[#0B1F3A] text-lg mb-1">{title}</h3>
    <p className="text-slate-600 font-medium mb-4">{description}</p>
    <Button 
      onClick={action}
      className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold btn-press"
    >
      {actionText}
    </Button>
  </div>
);

export default AjoSavings;
